import type { ICellRendererComp, ICellRendererParams } from "ag-grid-enterprise";

interface PostLinkCellRendererParams extends ICellRendererParams {
  // Row-data field holding the link target. Defaults to "post_url" so existing
  // callers (if any) are unaffected.
  urlField?: string;
  // Row-data field holding a display-only label rendered before the value (and outside
  // the link). Blank/absent → the value renders alone.
  prefixField?: string;
  // CSS class for that prefix label.
  prefixClass?: string;
}

// A real browser POST navigation (not fetch/AJAX), so a server-side redirect
// (e.g. impersonation swapping the session) actually navigates the browser.
export class PostLinkCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: PostLinkCellRendererParams): void {
    this.eGui = document.createElement("span");

    const urlField = params.urlField ?? "post_url";
    const data = params.data as Record<string, unknown> | undefined;
    const url = data?.[urlField] as string | undefined | null;
    const value = params.value ?? "";

    // Prepended, never part of `params.value`, so sort/filter/copy still see the bare value.
    const prefix = params.prefixField ? (data?.[params.prefixField] as string | undefined) : undefined;
    if (prefix) {
      const prefixEl = document.createElement("span");
      // Trailing space inside the span keeps the gap without relying on the class's margin.
      prefixEl.textContent = `${prefix} `;
      if (params.prefixClass) prefixEl.className = params.prefixClass;
      this.eGui.appendChild(prefixEl);
    }

    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.textContent = value;
      link.draggable = false; // avoid hijacking AG Grid's cell range drag-select
      link.style.color = "inherit";
      link.style.textDecoration = "none";

      // Actually in the DOM (not just constructed), so .submit() performs a full-page
      // POST navigation carrying the CSRF token as a form field.
      const form = document.createElement("form");
      form.method = "post";
      form.action = url;
      form.style.display = "none";

      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      link.addEventListener("click", (e) => {
        e.preventDefault();
        form.submit();
      });

      this.eGui.appendChild(link);
      this.eGui.appendChild(form);
    } else {
      // appendChild, not textContent — the latter would wipe an already-appended prefix.
      this.eGui.appendChild(document.createTextNode(value));
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }
}
